import IconSearch16 from '@warp-ds/icons/react/search-16';
import { Button, Pill } from '@warp-ds/react';
import cn from 'classnames';
import type { CSSProperties, ReactNode, RefObject } from 'react';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import FocusLock from 'react-focus-lock';
import { CloseButton } from '../../../components/closeButton/CloseButton';
import type { PrepackageDestination } from '../../types';
import { getDestinationKey } from '../utils/destinationPicker';
import { getDestinationName } from './labels';

type DestinationPickerPopoverProps = {
    variant?: 'popover' | 'modal';
    style?: CSSProperties;
    searchInputRef?: RefObject<HTMLInputElement>;
    searchQuery?: string;
    selectedDestinations: PrepackageDestination[];
    loading: boolean;
    error: boolean;
    isMaxedOut: boolean;
    children: ReactNode;
    onSearchQueryChange?: (query: string) => void;
    onRemoveDestination: (destination: PrepackageDestination) => void;
    onReset: () => void;
    onApply: () => void;
};

export const DestinationPickerPopover = ({
    variant = 'popover',
    style,
    searchInputRef,
    searchQuery = '',
    selectedDestinations,
    loading,
    error,
    isMaxedOut,
    children,
    onSearchQueryChange,
    onRemoveDestination,
    onReset,
    onApply,
}: DestinationPickerPopoverProps) => {
    const isModal = variant === 'modal';
    const scrollRef = useRef<HTMLDivElement>(null);
    const dialogRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const element = scrollRef.current;
        if (!isModal || !element) return;
        const stopTouchMovePropagation = (event: TouchEvent) => event.stopPropagation();
        element.addEventListener('touchmove', stopTouchMovePropagation, { passive: true });
        return () => element.removeEventListener('touchmove', stopTouchMovePropagation);
    }, [isModal]);

    useEffect(() => {
        if (!isModal) return;
        dialogRef.current?.focus();
    }, [isModal]);

    const selectedPills = selectedDestinations.map((destination) => (
        <Pill
            suggestion
            key={getDestinationKey(destination)}
            label={getDestinationName(destination)}
            canClose
            onClose={() => onRemoveDestination(destination)}
        />
    ));

    const popover = (
        <div
            id="prepackage-destination-popover"
            ref={dialogRef}
            className={cn('z-50 overflow-hidden s-bg', {
                'absolute mt-4 rounded-8 border s-border shadow-l': !isModal,
                'fixed inset-0 flex h-[100dvh] w-full flex-col rounded-0 border-0 shadow-none': isModal,
            })}
            style={style}
            {...(isModal
                ? {
                      role: 'dialog',
                      'aria-modal': true,
                      'aria-label': 'Hvor vil du reise?',
                      tabIndex: -1,
                  }
                : {})}
            data-testid="prepackageDestinationPicker">
            {isModal && (
                <div className="shrink-0 border-b s-border bg-white px-16 pb-12 pt-16">
                    <div className="mb-12 flex items-center justify-between">
                        <span className="text-20 font-bold">Hvor vil du reise?</span>
                        <CloseButton onClose={onApply} />
                    </div>
                    <div className="flex h-44 items-center gap-8 rounded-4 border s-border-strong bg-white px-12 focus-within:outline focus-within:outline-2 focus-within:outline-offset-[-2px] focus-within:s-border-focus">
                        <IconSearch16 aria-hidden className="shrink-0 s-icon-subtle" />
                        <input
                            ref={searchInputRef}
                            type="search"
                            value={searchQuery}
                            placeholder="Søk reisemål..."
                            autoComplete="off"
                            className="h-full min-w-0 flex-1 border-0 bg-transparent text-m outline-none"
                            onChange={(event) => onSearchQueryChange?.(event.target.value)}
                        />
                    </div>
                    {selectedDestinations.length > 0 && <div className="mt-12 flex flex-wrap gap-6">{selectedPills}</div>}
                </div>
            )}

            {!isModal && selectedDestinations.length > 0 && (
                <div className="flex flex-wrap gap-8 border-b s-border p-16">{selectedPills}</div>
            )}

            <div ref={scrollRef} className={cn({ 'min-h-0 flex-1 overflow-y-auto overscroll-contain': isModal })}>
                {loading && <div className="p-16 text-s s-text-subtle">Laster reisemål...</div>}
                {error && <div className="p-16 text-s s-text-negative">Kunne ikke laste reisemål.</div>}
                {!loading && !error && children}
            </div>

            <div
                className={cn('flex shrink-0 items-center justify-between border-t s-border px-16 py-12', {
                    'pb-[calc(env(safe-area-inset-bottom)+12px)]': isModal,
                })}>
                <Button type="button" secondary quiet onClick={onReset}>
                    Nullstill
                </Button>
                {isMaxedOut && <output className="text-s s-text-negative font-bold">Maks 10 destinasjoner</output>}
                <Button type="button" primary onClick={onApply}>
                    Bruk
                </Button>
            </div>
        </div>
    );

    if (isModal) {
        return createPortal(
            <FocusLock returnFocus={false} autoFocus={false} className="contents">
                {popover}
            </FocusLock>,
            document.body,
        );
    }

    return popover;
};
